import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Skeleton,
  Spin,
  Space,
  Tooltip,
  Typography,
  Dropdown,
  message,
  Menu,
  Tag,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import CreateCharity from "./CreateCharity";
import { useNavigate } from "react-router-dom";
import api from "../../../api";
import RejectionModal from "../Approval/RejectionModal";

const { Title, Text } = Typography;

const Charity = () => {
  const [openCreateCharity, setOpenCreateCharity] = useState(false);
  const [charityData, setCharityData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedDeleteItem, setSelectedDeleteItem] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [messageApi, contextHolder] = message.useMessage();


  const [pageSize, setPageSize] = useState(6);
  const [currentPage, setCurrentPage] = useState(1);

  const [editData, setEditData] = useState(null);
  const navigate = useNavigate();



  useEffect(() => {
    fetchCharity();
  }, []);

  const fetchCharity = async () => {
    try {
      setLoading(true);

      const payload = {
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "102",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };

      const res = await api.getCharity(payload);

      if (res?.data?.status === "1") {
        setCharityData(res.data.charity_data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteDonation = async () => {
    if (!selectedDeleteItem) return;

    try {
      setLoading(true);

      const payload = {
        fld_rf_id: selectedDeleteItem.fld_rf_id,
        synceddatetime: "2025-05-29 10:08:00",
        FormCode: "102",
        AppVersion: "1.0.0.0",
        ApiKey: "kavin",
        AppTypeNo: "3",
      };

      const res = await api.deleteCharity(payload);

      if (res?.data?.status === "1") {
        fetchCharity(); // 🔥 refresh list
        messageApi.success("Charity deleted successfully");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setOpenDeleteModal(false);     // ✅ close modal
      setSelectedDeleteItem(null);   // ✅ reset
    }
  };
  // 🔍 Filters
  const filteredData = charityData
    .filter((item) => {
      const title = item?.fld_charity_title || "";
      const status = item?.fld_status === "1" ? "Active" : "Closed";

      const matchesSearch = title
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "All" || status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // 🔥 Sort by creation date (latest first)
      return new Date(b.fld_system_inserted_datetime) - new Date(a.fld_system_inserted_datetime);
    });

  // 📄 Pagination
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(
    startIndex,
    startIndex + pageSize
  );
  const totalPages = Math.ceil(filteredData.length / pageSize);

  const pageMenu = (
    <Menu
      items={[6, 10, 20].map((num) => ({
        key: num,
        label: num,
        onClick: () => {
          setPageSize(num);
          setCurrentPage(1);
        },
      }))}
    />
  );

  // 🎯 Handlers
  const handleOpenCreate = () => {
    setEditData(null);
    setOpenCreateCharity(true);
  };

  const handleEdit = (item) => {
    setEditData(item);
    setOpenCreateCharity(true);
  };

  const handleCloseModal = () => {
    setOpenCreateCharity(false);
    setEditData(null);
  };

  return (
    <>
      {/* Modal */}
      {contextHolder}
      {openCreateCharity && (
        <CreateCharity
          visible={openCreateCharity}
          onCancel={handleCloseModal}
          editdata={editData}
          onSuccess={() => {
            handleCloseModal();
            fetchCharity(); // 🔥 refresh list
          }}
        />
      )}

      {openDeleteModal && (
        <RejectionModal
          title="Delete Charity"
          content="Are you sure you want to delete this charity? This action cannot be undone."
          buttonName="Delete"
          open={openDeleteModal}
          onClose={() => {
            setOpenDeleteModal(false);
            setSelectedDeleteItem(null);
          }}
          onSubmit={handleDeleteDonation}
        />
      )}

      <Spin spinning={loading}>
        <div className="review-page">
          {/* 🔥 Header */}
          <Flex
            className="review-header"
            justify="space-between"
            align="center"
            style={{ marginBottom: 16 }}
          >
            <Input
              className="review-search"
              prefix={<SearchOutlined />}
              placeholder="Search charity..."
              style={{ width: 300 }}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />

            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenCreate}
                style={{ background: "#0072b1" }}
              >
                Create Charity
              </Button>
            </Space>
          </Flex>

          {/* 🔥 Title */}
          <div className="review-container">
            <div className="review-heading">
              <Title level={4} style={{ margin: 0 }}>
                Charity Management
              </Title>
              <Text type="secondary">
                Manage, publish, and track charity items
              </Text>
            </div>

            {/* 🔥 Filters */}
            <div className="review-filters">
              <div className="filter-buttons">
                <Dropdown
                  overlay={
                    <Menu
                      items={[
                        {
                          key: "All",
                          label: "All",
                          onClick: () => setStatusFilter("All"),
                        },
                        {
                          key: "Active",
                          label: "Active",
                          onClick: () => setStatusFilter("Active"),
                        },
                        {
                          key: "Closed",
                          label: "Closed",
                          onClick: () => setStatusFilter("Closed"),
                        },
                      ]}
                    />
                  }
                >
                  <Button>
                    {statusFilter} <DownOutlined />
                  </Button>
                </Dropdown>

                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setStatusFilter("All");
                    setSearchTerm("");
                    setCurrentPage(1);
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>

            {/* 🔥 Cards */}
            <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
              {loading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <Card
                    key={index}
                    style={{
                      borderRadius: 10,
                      padding: "8px 12px",
                    }}
                  >
                    <Flex justify="space-between" align="center">
                      {/* Left */}
                      <Flex gap={12} align="center">
                        <Skeleton.Image
                          active
                          style={{
                            width: 70,
                            height: 70,
                            borderRadius: 8,
                          }}
                        />

                        <div style={{ width: 450 }}>
                          <Skeleton
                            active
                            paragraph={{
                              rows: 2,
                              width: ["60%", "90%"],
                            }}
                            title={{ width: "40%" }}
                          />

                          <Flex gap={8} style={{ marginTop: 6 }}>
                            <Skeleton.Button
                              active
                              size="small"
                              style={{ width: 100 }}
                            />
                            <Skeleton.Button
                              active
                              size="small"
                              style={{ width: 120 }}
                            />
                          </Flex>
                        </div>
                      </Flex>

                      {/* Right Buttons */}
                      <Flex gap={8}>
                        <Skeleton.Button active size="small" />
                        <Skeleton.Button active size="small" />
                      </Flex>
                    </Flex>
                  </Card>
                ))
              ) : paginatedData.length === 0 ? (
                <Card>
                  <Empty
                    description="No charity data"
                    image={<InboxOutlined style={{ fontSize: 40 }} />}
                  />
                </Card>
              ) : (
                paginatedData.map((item) => (
                  <Card
                    key={item.fld_rf_id}
                    hoverable
                    onClick={() => {
                      navigate(`charity/${item.fld_rf_id}`, {
                        state: {
                          charityData: {
                            id: item.fld_charity_id,
                            title: item.fld_charity_title,
                            description: item.fld_description,
                            itemName: item.fld_item_name,
                            location: item.fld_location,
                            charityPrice: item.fld_charity_price_in_inr,
                            marketPrice: item.fld_market_price_inr,
                            totalQty: item.fld_total_quantity_offered,
                            userLimit: item.fld_user_quantity_limit,
                            email: item.fld_contact_email,
                            mobile: item.fld_contact_mobile,
                            startDate: item.fld_start_date,
                            endDate: item.fld_end_date,
                            createdDate: item.fld_charity_creation_date,
                            status: item.fld_is_active === "1" ? "Active" : "Closed",
                            image: item.fld_charity_image_path,

                            // ✅ ADD THIS
                            beneficiaryName: item.fld_beneficiary_name,
                          },
                        },
                      });

                    }

                    }
                  >
                    <Flex justify="space-between" align="center">
                      {/* Left */}
                      <Flex gap={16}>
                        <img
                          src={item.fld_charity_image_path || "https://via.placeholder.com/80"}
                          alt="charity"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />

                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <Text strong>{item.fld_charity_title || "Untitled"}</Text>

                            {/* <Tag color={item.fld_status === "1" ? "green" : "red"}>
                              {item.fld_status === "1" ? "Active" : "Closed"}
                            </Tag> */}
                          </div>

                          <div style={{ marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <Tag color="purple">Item: {item.fld_item_name}</Tag>
                            <Tag color="orange">Limit: {item.fld_user_quantity_limit} / user</Tag>
                          </div>

                          <div style={{ color: "#888", maxWidth: 400 }}>
                            <div
                              style={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {item.fld_description || "No description"}
                            </div>
                          </div>

                          <div style={{ marginTop: 4, display: "flex", gap: 8, alignItems: "center" }}>
                            <Tag color="blue">{item.fld_location}</Tag>

                            <span style={{ fontWeight: 600, color: "#52c41a" }}>
                              ₹ {item.fld_charity_price_in_inr}
                            </span>

                            <span
                              style={{
                                textDecoration: "line-through",
                                color: "#999",
                                fontSize: 12,
                              }}
                            >
                              ₹ {item.fld_market_price_inr}
                            </span>
                          </div>
                        </div>
                      </Flex>

                      {/* Right */}
                      <Space>
                        <Tooltip title="Edit this Charity">
                          <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(item);
                            }}
                          >
                            Edit
                          </Button>
                        </Tooltip>

                        <Tooltip title="Delete this Charity">
                          <Button
                            icon={<DeleteOutlined />}
                            danger
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedDeleteItem(item);   // ✅ store item
                              setOpenDeleteModal(true);
                            }}
                          >
                            Delete
                          </Button>
                        </Tooltip>
                      </Space>
                    </Flex>
                  </Card>
                ))
              )}
            </div>

            {/* 🔥 Pagination */}
            <Flex
              justify="center"
              align="center"
              gap={10}
              style={{ marginTop: 20 }}
            >
              <Button
                icon={<LeftOutlined />}
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              />

              <span>
                Page {currentPage} of {totalPages || 1}
              </span>

              <Button
                icon={<RightOutlined />}
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              />

              <Dropdown overlay={pageMenu}>
                <Button>
                  {pageSize} / page <DownOutlined />
                </Button>
              </Dropdown>
            </Flex>
          </div>
        </div>
      </Spin>
    </>
  );
};

export default Charity;